import React, { useState, useCallback } from 'react';
import { withTranslation, useTranslation } from 'react-i18next';
import { Tag, Tooltip, Input, message } from 'antd';
import { InputContainer } from '../../../components/Modals/styles';
import { SettingsHeading } from '../../SidebarMenu/styles';
import { updateTags } from '../../../utils/services/complexDocumentsAPI';
import {
  COMPLEX_DOCUMENT_CONTEXT_KEYS,
  useDocumentContext,
} from '../../../context/complexDocumentContext';
import { useArtboardContext, CONTEXT_KEYS } from '../../../context/complexArtboardContext';
import { MANAGE_TEMPLATE_TAGS_MODAL } from '../../../components/Modals/translationValues';
import { MANAGE_TAGS_AND_FOLDER } from '../../../components/FileDropdown/translationValues';

const NAMESPACES = ['MANAGE_TAGS_AND_FOLDER', 'MANAGE_TEMPLATE_TAGS_MODAL'];

const FileSettings = () => {
  const { t } = useTranslation(NAMESPACES);

  const customSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.TAGS], []);
  const [tags, store] = useDocumentContext(customSelector, ``);

  const currentDesignSelector = useCallback(store => store[CONTEXT_KEYS.CURRENT_DESIGN], []);
  const [currentDesign] = useArtboardContext(currentDesignSelector, ``);

  const isOpenDesignSelector = useCallback((store) => store[CONTEXT_KEYS.IS_OPEN_DESIGN], []);
  const [isOpenDesign, updateArtboard] = useArtboardContext(isOpenDesignSelector, `FileSettings`);

  const [inputValue, setInputValue] = useState('');
  const [tagsList, updateTagList] = useState(tags);

  // When the 'Enter' key is pressed, this function is triggered to update the list of tags.
  const handleTagAdd = useCallback(() => {
    // Validation 1: If the input value is empty, do not consider it as a new tag.
    if (inputValue.trim().length === 0) return;
    // Validation 2: Check if the tag already exists.
    const trimmedTag = inputValue.trim();
    const exists = tagsList.some(tag => tag.toLowerCase() === trimmedTag.toLowerCase());
    if (exists) {
      message.error(
        t(
          MANAGE_TEMPLATE_TAGS_MODAL.TAGS_ALREADY_EXIST_ERROR_MESSAGE.key,
          MANAGE_TEMPLATE_TAGS_MODAL.TAGS_ALREADY_EXIST_ERROR_MESSAGE.value,
        ),
      );
    } else {
      const tagAdded = [...tagsList, trimmedTag];
      setInputValue('');
      updateTagList(tagAdded);
      handleSave(tagAdded);
    }
  }, [inputValue, tagsList]);

  // This function is triggered when the close icon is clicked to remove a tag from the list.
  const handleTagRemove = useCallback(
    value => {
      const tagRemoved = tagsList.filter(tag => tag !== value);
      updateTagList(tagRemoved);
      handleSave(tagRemoved);
    },
    [tagsList],
  );

  // This function is triggered when the save button is clicked. The save button will be enabled only if any tags are added or removed.
  const handleSave = useCallback(
    async tags => {
      const response = await updateTags({
        id: store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.ID],
        tags: tags.join(','),
        type: currentDesign,
      });
      if (response.statusCode === 1901) {
        message.success(
          t(
            MANAGE_TEMPLATE_TAGS_MODAL.TAGS_UPDATED_SUCCESS_MESSAGE.key,
            MANAGE_TEMPLATE_TAGS_MODAL.TAGS_UPDATED_SUCCESS_MESSAGE.value,
          ),
        );
        store.set({ [COMPLEX_DOCUMENT_CONTEXT_KEYS.TAGS]: tags });
      } else {
        message.error(
          t(
            MANAGE_TEMPLATE_TAGS_MODAL.FAILED_TO_FETCH_TAGS_ERROR_MESSAGE.key,
            MANAGE_TEMPLATE_TAGS_MODAL.FAILED_TO_FETCH_TAGS_ERROR_MESSAGE.value,
          ),
        );
      }
    },
    [currentDesign],
  );

  function handleOpenDesign() {
      updateArtboard({
            [CONTEXT_KEYS.SHOW_SIGNUP_PROMPT]: true
      });
  }

  return (
    <>
      <SettingsHeading docName>
        <div>
          {t(
            MANAGE_TAGS_AND_FOLDER.MANAGE_TAGS_BUTTON.key,
            MANAGE_TAGS_AND_FOLDER.MANAGE_TAGS_BUTTON.value,
          )}
        </div>
      </SettingsHeading>

      <div>
        {tagsList.map((tag, index) => {
          const isLongTag = tag.length > 20;
          const tagElem = (
            <Tag
              className="edit-tag"
              key={tag}
              closable={true}
              onClose={() => handleTagRemove(tag)}
              color="#df4c35"
            >
              <span>{isLongTag ? `${tag.slice(0, 20)}...` : tag}</span>
            </Tag>
          );
          return isLongTag ? (
            <Tooltip title={tag} key={tag}>
              {tagElem}
            </Tooltip>
          ) : (
            tagElem
          );
        })}
        <InputContainer textArea password>
          <Input
            name="tagName"
            type="text"
            className="manageTag-input"
            value={inputValue}
            readOnly={isOpenDesign}
            onClick={isOpenDesign && handleOpenDesign}
            onChange={e => setInputValue(e.target.value)}
            onPressEnter={() => handleTagAdd()}
            placeholder={t(
              MANAGE_TEMPLATE_TAGS_MODAL.ADD_TAGS_PLACEHOLDER.key,
              MANAGE_TEMPLATE_TAGS_MODAL.ADD_TAGS_PLACEHOLDER.value,
            )}
            autoComplete={'off'}
          />
        </InputContainer>
      </div>
    </>
  );
};

export default withTranslation(NAMESPACES)(FileSettings);
